import { faSpinner, faWallet } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as token from '@solana/spl-token'
import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, PublicKey } from '@solana/web3.js'
import { useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Copyable } from '~/components/ui/Copyable'
import { formatWalletAddress } from '~/features/bank/utils'
import { useAvailableWallets } from '~/features/wallet/hooks/useAvailableWallets'
import { useSelectWallet } from '~/features/wallet/hooks/useSelectWallet'
import { useDepositOptions } from '../api/useDepositOptions'
const usdcIcon = new URL('~/features/bank/assets/usdc.png', import.meta.url).toString()
const solIcon = new URL('~/features/bank/assets/solana.png', import.meta.url).toString()

export const WalletSelector = () => {
    const {
        data: { cluster_url, token_mint },
    } = useDepositOptions()
    const { publicKey } = useWallet()
    const [walletUsdcAmount, setWalletUsdcAmount] = useState<number>(undefined)
    const [walletSolAmount, setWalletSolAmount] = useState<number>(undefined)

    // This selector rendered `wallets` unfiltered -- every adapter, including ones this browser
    // cannot reach. Clicking those did nothing and reported nothing, which is a bad failure mode
    // on the page where players put money in. Paying needs `signTransaction`, so require it.
    // Fractal is still excluded by name: it *has* the method but does not support transaction
    // signing the same way, which a capability check cannot express.
    const { available } = useAvailableWallets({ require: 'transaction', exclude: ['Fractal'] })
    const { choose, pending, error, wallet } = useSelectWallet()

    const selected = wallet?.adapter.name

    const connection = useMemo(() => {
        if (cluster_url) return new Connection(cluster_url, 'confirmed')
    }, [cluster_url])

    const selectWallet = (name) => {
        choose(name)
        setWalletUsdcAmount(undefined)
    }

    useEffect(() => {
        if (publicKey && token_mint && connection) {
            token
                .getAssociatedTokenAddress(new PublicKey(token_mint), publicKey)
                .then(async (ata) => {
                    const accountData = await token.getAccount(connection, ata, 'confirmed')
                    setWalletUsdcAmount(Number(accountData.amount) / 1_000_000)
                })
                // A wallet that has never held USDC has no associated token account, and
                // `getAccount` throws `TokenAccountNotFoundError` for it. Unhandled, that was an
                // console rejection on every freshly connected wallet. No account means no balance.
                .catch(() => setWalletUsdcAmount(0))

            connection
                .getBalance(publicKey)
                .then((balance) => setWalletSolAmount(balance / 1_000_000_000))
                .catch(() => setWalletSolAmount(undefined))
        }
    }, [publicKey, token_mint, connection])

    return (
        <section className='flex flex-col gap-2 items-center font-bold max-w-lg w-full'>
            <div className='flex flex-row items-center gap-2 w-full'>
                <FontAwesomeIcon icon={faWallet} className='h-6 w-6 px-2' />
                <div className='flex-1 flex flex-row items-center justify-center bg-white/5 p-1 pr-10'>
                    {available.length > 0 ? (
                        available.map((w, i) => (
                            <button
                                key={i}
                                className={twMerge(
                                    'relative p-2 border rounded',
                                    w.adapter.name == selected ? 'border-white/20' : ' border-transparent'
                                )}
                                onClick={() => selectWallet(w.adapter.name)}
                            >
                                <img src={w.adapter.icon} className='h-6 w-6' />
                                {w.adapter.name == pending && (
                                    <div className='absolute top-0 left-0 w-full h-full bg-black/50 flex flex-row items-center justify-center'>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    </div>
                                )}
                            </button>
                        ))
                    ) : (
                        <span className='px-2 py-1 text-xs font-normal text-white/50'>
                            No Solana wallet detected. Install Phantom or Solflare and reload.
                        </span>
                    )}
                </div>
            </div>
            {error && <div className='text-xs font-bold text-red-400'>{error}</div>}
            <div className='text-xs h-4 text-white/80'>
                {publicKey && (
                    <Copyable value={publicKey.toString()}>{formatWalletAddress(publicKey.toString())}</Copyable>
                )}
            </div>
            <div className=' flex flex-row gap-2 text-xs h-4 text-white/80'>
                {walletUsdcAmount && (
                    <div className='flex flex-row gap-1 items-center'>
                        <span>{walletUsdcAmount.toFixed(2)}</span>
                        <img src={usdcIcon} className='h-4 w-4' />
                    </div>
                )}
                {walletSolAmount && (
                    <div className='flex flex-row gap-1 items-center'>
                        <span>{walletSolAmount.toFixed(4)}</span>
                        <img src={solIcon} className='h-4 w-4' />
                    </div>
                )}
            </div>
        </section>
    )
}
